import { z } from 'zod';
import { phoneSchema } from './schemas.ts';

/* Inventory: flats, plots, villas… -------------------------------------------------------------- */

export const UNIT_TYPES = [
  { key: 'flat', label: 'Flat' },
  { key: 'plot', label: 'Plot' },
  { key: 'villa', label: 'Villa' },
  { key: 'house', label: 'House' },
  { key: 'shop', label: 'Shop' },
  { key: 'office', label: 'Office' },
  { key: 'other', label: 'Other' },
] as const;
export type UnitType = (typeof UNIT_TYPES)[number]['key'];

export const UNIT_STATUSES = [
  { key: 'available', label: 'Available', color: '#16a34a' },
  { key: 'blocked', label: 'On hold', color: '#d97706' },
  { key: 'sold', label: 'Sold', color: '#4f46e5' },
  { key: 'inactive', label: 'Not for sale', color: '#64748b' },
] as const;
export type UnitStatus = (typeof UNIT_STATUSES)[number]['key'];

const text = (max: number) => z.string().trim().max(max).optional().transform((v) => v || null);
const num = (min: number, max: number) =>
  z.union([z.number(), z.string().trim()]).optional()
    .transform((v) => (v === undefined || v === '' ? null : Number(String(v).replace(/[,₹\s]/g, ''))))
    .refine((v) => v === null || (Number.isFinite(v) && v >= min && v <= max), { error: `Enter a number between ${min} and ${max.toLocaleString('en-IN')}.` });

export const unitSchema = z.object({
  project: z.string().trim().min(1, { error: 'Enter the project name.' }).max(120),
  code: z.string().trim().min(1, { error: 'Enter the unit number, e.g. A-101.' }).max(40),
  unitType: z.enum(UNIT_TYPES.map((t) => t.key) as [UnitType, ...UnitType[]]).default('flat'),
  bedrooms: num(0, 20),
  sizeSqft: num(1, 10_000_000),
  block: text(60),
  floor: text(20),
  facing: text(30),
  price: num(0, 1_000_000_000_000),
  notes: text(1000),
});
export type UnitInput = z.input<typeof unitSchema>;

/** Many units at once: "A-101 to A-110" style ranges are expanded on the server. */
export const unitBatchSchema = unitSchema.omit({ code: true }).extend({
  // Not trimmed at the end: "Plot " makes "Plot 7".
  prefix: z.string().max(20).default('').transform((v) => v.trimStart()),
  from: z.coerce.number().int().min(0).max(100_000),
  to: z.coerce.number().int().min(0).max(100_000),
}).refine((v) => v.to >= v.from && v.to - v.from < 500, { error: 'Up to 500 units at a time, and “to” must not be smaller than “from”.', path: ['to'] });

export const unitHoldSchema = z.object({
  leadId: z.uuid({ error: 'Choose the lead.' }),
  days: z.coerce.number().int().min(1).max(90).default(7),
});

export const unitSellSchema = z.object({
  leadId: z.uuid({ error: 'Choose the buyer (lead).' }),
  price: num(0, 1_000_000_000_000),
});

/** "₹45,00,000" → "₹45 L", "₹1,20,00,000" → "₹1.2 Cr": how Indian sales teams say prices. */
export function shortRupees(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1_00_00_000) return `₹${+(n / 1_00_00_000).toFixed(2)} Cr`;
  if (n >= 1_00_000) return `₹${+(n / 1_00_000).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN')}`;
}

/* Landing pages ----------------------------------------------------------------------------------- */

export const landingPageSchema = z.object({
  title: z.string().trim().min(2, { error: 'Give the page a name.' }).max(120),
  slug: z.string().trim().toLowerCase().min(3, { error: 'At least 3 letters.' }).max(60)
    .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, { error: 'Use small letters, numbers and dashes, e.g. green-meadows-offer.' }),
  headline: z.string().trim().min(3, { error: 'Write a headline.' }).max(160),
  subheadline: text(300),
  body: text(4000),
  highlights: z.array(z.string().trim().min(1).max(120)).max(8).default([]),
  ctaLabel: z.string().trim().min(2).max(40).default('Get a call back'),
  whatsappPhone: z.union([phoneSchema, z.literal('')]).optional().transform((v) => v || null),
  formId: z.uuid({ error: 'Choose the form visitors fill in.' }),
  isActive: z.boolean().default(true),
});
export type LandingPageInput = z.input<typeof landingPageSchema>;

/* Live team status ---------------------------------------------------------------------------------- */

export const AWAY_STATUSES = [
  { key: 'meeting', label: 'In a meeting', emoji: '🤝' },
  { key: 'demo', label: 'Giving a demo', emoji: '🖥️' },
  { key: 'field', label: 'Out on a visit', emoji: '🚗' },
  { key: 'lunch', label: 'At lunch', emoji: '🍱' },
  { key: 'break', label: 'On a break', emoji: '☕' },
  { key: 'training', label: 'In training', emoji: '📚' },
] as const;
export type AwayStatus = (typeof AWAY_STATUSES)[number]['key'];

/** What a team member is doing right now (worked out on the server). */
export type PresenceState = 'on_call' | AwayStatus | 'online' | 'idle' | 'offline';

export const PRESENCE_LABELS: Record<'on_call' | 'online' | 'idle' | 'offline', { label: string; color: string }> = {
  on_call: { label: 'On a call', color: '#dc2626' },
  online: { label: 'Online', color: '#16a34a' },
  idle: { label: 'Idle', color: '#d97706' },
  offline: { label: 'Offline', color: '#94a3b8' },
};
